import { mdiInformationOutline, mdiTuneVertical, mdiViewWeekOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Tooltip } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { BaseButton, Popover, PopoverHeader, PopoverItem, TooltipTransitionComponent } from '../../../base';
import { ITableColumn } from '../../data-table.presenter';
import * as S from './settings.styles';

interface ISettingsProps<D extends object> {
    columns?: ITableColumn<D>[];
    hiddenColumnIds: string[];
    onColumnToggle: (columnId: string) => void;
    variant?: 'small';
    pageSize?: number;
    pageSizeOptions?: number[];
    onPageSizeChange?: (pageSize: number) => void;
}

export class Settings<D extends object = {}> extends React.PureComponent<ISettingsProps<D>> {
    private static readonly DEFAULT_PAGE_SIZE_OPTIONS: number[] = [5, 10, 20, 30, 50, 100];

    public render(): React.ReactElement {
        const hasRowsPerPage = this.props.pageSize !== undefined && !!this.props.onPageSizeChange;

        return (
            <S.SettingsContainer>
                <S.ButtonsGroup>
                    {hasRowsPerPage && (
                        <Popover renderButton={this.renderRowsPerPageButton} align="right" selectable={true} pinned={true}>
                            <PopoverHeader>Rows per page</PopoverHeader>
                            {this.renderPageSizeItems()}
                        </Popover>
                    )}
                    <Popover renderButton={this.renderColumnsButton} align="right" selectable={true} pinned={true}>
                        <PopoverHeader>Select visible columns</PopoverHeader>
                        {this.renderItems()}
                    </Popover>
                </S.ButtonsGroup>
            </S.SettingsContainer>
        );
    }

    private renderItems = (): React.ReactElement[] => {
        const { columns, hiddenColumnIds, onColumnToggle } = this.props;
        if (!columns) {
            return [];
        }

        return columns
            .filter(column => column.Header)
            .map(column => {
                if (!column.id) {
                    throw new Error('Column must have an id!');
                }

                const columnId: string = column.id;
                const header = _.isFunction(column.Header) 
                    ? _.startCase(columnId) 
                    : _.isString(column.Header) 
                        ? column.Header 
                        : React.createElement(column.Header as any);

                const handleSelectedChange = (): void => onColumnToggle(columnId);
                return (
                    <PopoverItem
                        selected={!hiddenColumnIds.includes(columnId)}
                        key={columnId}
                        onClick={handleSelectedChange}>
                        <S.ItemWrapper>
                            <span>{header}</span>
                            {column.description ? (
                                <Tooltip
                                    placement="top"
                                    TransitionComponent={TooltipTransitionComponent}
                                    title={<span>{column.description}</span>}
                                    enterTouchDelay={0}
                                    disableInteractive={true}>
                                    <S.IconWrapper>
                                        <Icon path={mdiInformationOutline} size={0.75} />
                                    </S.IconWrapper>
                                </Tooltip>
                            ) : null}
                        </S.ItemWrapper>
                    </PopoverItem>
                );
            });
    };

    private renderPageSizeItems = (): React.ReactElement[] => {
        const { pageSize, onPageSizeChange } = this.props;
        if (pageSize === undefined || !onPageSizeChange) {
            return [];
        }

        const pageSizeOptions = this.props.pageSizeOptions || Settings.DEFAULT_PAGE_SIZE_OPTIONS;
        return pageSizeOptions.map(num => (
            <PopoverItem
                selected={num === pageSize}
                key={`pagesize-option-${num}`}
                onClick={() => num !== pageSize && onPageSizeChange(num)}>
                {num}
            </PopoverItem>
        ));
    };

    private renderRowsPerPageButton = (): React.ReactElement => {
        return (
            <Tooltip placement="top-start" title="Rows per page" TransitionComponent={TooltipTransitionComponent}>
                <div>{this.renderActionButton(mdiTuneVertical, 'Rows per page')}</div>
            </Tooltip>
        );
    };

    private renderColumnsButton = (): React.ReactElement => {
        if (this.props.variant === 'small') {
            return this.renderActionButton(mdiViewWeekOutline, 'Select visible columns');
        }
        return (
            <BaseButton variant="fab-light">
                <Icon path={mdiViewWeekOutline} size={0.7} />
            </BaseButton>
        );
    };

    private renderActionButton = (path: string, ariaLabel: string): React.ReactElement => (
        <S.ActionButton variant="outlined" size="small" type="button" aria-label={ariaLabel}>
            <Icon path={path} size={0.7} />
        </S.ActionButton>
    );
}
